import { Authenticator } from '@aws-amplify/ui-react';
import type { ReactNode } from 'react';
import './authTheme.css';

interface AuthShellProps {
  /** Render-prop receives the Authenticator render-prop arg (already-signed-in state). */
  children: (props: { signOut?: () => void }) => ReactNode;
}

/**
 * Wraps Amplify's Authenticator in a branded split-pane shell — form on the
 * left, value-prop sidebar on the right (cream + sage + terracotta). Replaces
 * the default Amplify chrome (which is functional but looks like a generic
 * AWS form). All visual customization is in `authTheme.css` via CSS custom
 * properties Amplify exposes; this file is just the layout + copy.
 */
export function AuthShell({ children }: AuthShellProps) {
  return (
    <Authenticator
      // The Cognito pool is configured `UsernameAttributes: [email]`, so the
      // username field IS the email — no need to add `email` to signUpAttributes
      // (that would render a redundant second email input). The I18n overrides
      // in amplifyConfig.ts rename the visible label from "Username" to "Email"
      // across every screen.
      hideSignUp={false}
      components={{
        Header() {
          return (
            <>
              <div className="crea-auth-brand">
                <div className="crea-auth-brand-mark">Z</div>
                <span>Zynplex</span>
              </div>
              <h1 className="crea-auth-title">Sign in to your account</h1>
              <p className="crea-auth-subtitle">
                Access your portal to manage your CREA DDF® feeds, generate API
                keys, and monitor your subscription.
              </p>
            </>
          );
        },
        SignIn: {
          Header() {
            return null;
          },
        },
        SignUp: {
          Header() {
            return (
              <>
                <h2 className="crea-auth-title" style={{ fontSize: 22, marginTop: 16 }}>
                  Create an account
                </h2>
                <p className="crea-auth-subtitle" style={{ marginBottom: 16 }}>
                  7-day free trial. No charge today.
                </p>
              </>
            );
          },
        },
      }}
    >
      {({ signOut }) => <>{children({ signOut })}</>}
    </Authenticator>
  );
}

/**
 * The left-of-form pane: brand identity + value props. Rendered as a sibling
 * to AuthShell, NOT inside it, because Amplify's Authenticator wraps its own
 * children only when signed-in. The split-pane shell lives one level up.
 */
export function AuthSidePane() {
  return (
    <aside className="crea-auth-side-pane">
      <div className="crea-auth-side-content">
        <h2 className="crea-auth-side-title">Modern API over Canadian MLS® listings</h2>
        <p className="crea-auth-side-lede">
          Connect your CREA DDF feed, generate API keys, and ship a fast
          property-search experience on your site — without writing OData
          queries or running your own ETL.
        </p>
        <ul className="crea-auth-points">
          <li className="crea-auth-point">
            <span className="crea-auth-point-check">✓</span>
            <span>Search, single listing, history, media — clean REST endpoints</span>
          </li>
          <li className="crea-auth-point">
            <span className="crea-auth-point-check">✓</span>
            <span>PostGIS-powered radius and polygon search</span>
          </li>
          <li className="crea-auth-point">
            <span className="crea-auth-point-check">✓</span>
            <span>Full CREA DDF data sync, refreshed every 10 minutes</span>
          </li>
          <li className="crea-auth-point">
            <span className="crea-auth-point-check">✓</span>
            <span>7-day free trial — no charge until you connect your first feed</span>
          </li>
        </ul>
      </div>
    </aside>
  );
}
